import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2, Key } from "react-feather";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import UserModal from "../../core/modals/settings/UserModal";
import { fetchUsers, deleteUserThunk } from "../../core/redux/slices/userSlice";
import { resetUserPassword } from "../../core/api/userService";

const UserList = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((s) => s.users);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => { dispatch(fetchUsers()); }, [dispatch]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter((u) => !q || u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q));
  }, [list, search]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord({ ...rec, id: rec.userID ?? rec.id }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditRecord(null); dispatch(fetchUsers()); };

  const handleResetPassword = async (rec) => {
    const { value: newPass, isConfirmed } = await Swal.fire({
      title: `Reset password for ${rec.fullName}`,
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password",
      inputAttributes: { autocomplete: "new-password", minlength: 6 },
      showCancelButton: true,
      confirmButtonText: "Reset",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed || !newPass) return;
    try {
      await resetUserPassword(rec.userID ?? rec.id, { newPassword: newPass });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Password reset", showConfirmButton: false, timer: 2000 });
    } catch {
      Swal.fire("Error", "Failed to reset password.", "error");
    }
  };

  const columns = [
    { title: "Full Name", dataIndex: "fullName", sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || "") },
    { title: "Username", dataIndex: "username", width: 130 },
    { title: "Email", dataIndex: "email" },
    { title: "Role", dataIndex: "roleName", width: 130 },
    {
      title: "Status", dataIndex: "status", width: 90,
      render: (v) => <span className={`badge bg-${v === "Active" ? "success" : "danger"}`}>{v}</span>,
    },
    {
      title: "Action", dataIndex: "action", width: 130,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" title="Edit" onClick={() => openEdit(rec)}><Edit size={15} /></button>
          <button className="me-2 p-2 btn btn-sm text-warning" title="Reset Password" onClick={() => handleResetPassword(rec)}><Key size={15} /></button>
          <button className="p-2 btn btn-sm text-danger" title="Delete"
            onClick={() => confirmDelete(() => dispatch(deleteUserThunk(rec.userID ?? rec.id)), rec.fullName)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="User Management" subtitle="Manage system users and their access" onAdd={openAdd} addLabel="Add User" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-input">
                <input type="text" placeholder="Search by name, email or username..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <UserModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default UserList;
